import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Instagram, Youtube, Linkedin, MessageCircle, Send, Mail, ChevronRight } from 'lucide-react';
import { db, collection, addDoc } from './firebase';

import { BannerAd } from './AdSense';

import logo from '../assets/logo.svg';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setIsSubmitting(true);
    try {
      await addDoc(collection(db, 'newsletter'), {
        email: email,
        timestamp: new Date()
      });
      setSuccessMessage('Subscribed successfully!');
      setEmail('');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error adding document: ', error);
      setSuccessMessage('Something went wrong. Please try again.');
    }
    setIsSubmitting(false);
  };



  const FooterLink = ({ to, children, external }) => {
    const Component = external ? 'a' : Link;
    const props = external ? { href: to, target: "_blank", rel: "noopener noreferrer" } : { to };

    return (
      <motion.div whileHover={{ x: 4 }}>
        <Component
          {...props}
          className="text-muted-foreground hover:text-primary text-sm font-bold flex items-center gap-1.5 transition-colors no-underline mb-2.5"
        >
          <ChevronRight size={14} className="text-primary opacity-50" />
          {children}
        </Component>
      </motion.div>
    );
  };

  return (
    <footer className="bg-background border-t border-border pt-10 pb-8">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Quick Links */}
          <div>
            <h3 className="text-foreground text-[15px] font-black mb-5 uppercase tracking-widest">
              Quick Links
            </h3>
            <FooterLink to="/">Home</FooterLink>
            <FooterLink to="/about">About Us</FooterLink>
            <FooterLink to="/contact">Contact</FooterLink>
            <FooterLink to="/profile">My Profile</FooterLink>
          </div>

          {/* Resources */}
          <div>
            <h3 className="text-foreground text-[15px] font-black mb-5 uppercase tracking-widest">
              Resources
            </h3>
            <FooterLink to="/tech-library">Tech Library</FooterLink>
            <FooterLink to="/event">Events</FooterLink>
            <FooterLink to="/feedback">Community Feedback</FooterLink>
            <FooterLink to="/privacy-policy">Privacy Policy</FooterLink>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-foreground text-[15px] font-black mb-5 uppercase tracking-widest">
              Get in Touch
            </h3>
            <div className="flex flex-col gap-3">
              <a href="mailto:snaped4338@gmail.com" className="text-muted-foreground hover:text-primary text-sm flex items-center gap-2.5 no-underline transition-colors">
                <Mail size={16} className="text-primary" /> snaped4338@gmail.com
              </a>
              <div className="flex gap-2.5 mt-1">
                {[
                  { icon: <Instagram />, url: 'https://www.instagram.com/snapedcodecampus' },
                  { icon: <Youtube />, url: 'https://youtube.com/@snapedcodecampus' },
                  { icon: <Linkedin />, url: 'https://www.linkedin.com/company/snaped-code-campus' },
                  { icon: <MessageCircle />, url: 'https://wa.me/9871391381' }
                ].map((social, i) => (
                  <motion.a
                    key={i}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.1, backgroundColor: 'rgba(var(--primary), 0.1)' }}
                    className="w-8 h-8 rounded-lg bg-card border border-border text-foreground flex items-center justify-center text-base transition-all duration-200 hover:border-primary/50"
                  >
                    {social.icon}
                  </motion.a>
                ))}
              </div>
            </div>
          </div>

          {/* Newsletter */}
          <div>
            <h3 className="text-foreground text-[15px] font-black mb-5 uppercase tracking-widest">
              Newsletter
            </h3>
            <p className="text-muted-foreground text-xs leading-relaxed mb-4 font-medium">
              Stay updated with our latest IPU notes and tech courses.
            </p>
            <form onSubmit={handleSubscribe} className="relative">
              <input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full h-11 bg-accent/30 border border-border rounded-xl px-4 pr-11 text-foreground text-[13px] outline-none focus:border-primary transition-all duration-300"
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="absolute right-1 top-1 w-9 h-9 bg-primary rounded-lg text-primary-foreground flex items-center justify-center cursor-pointer transition-transform hover:scale-105 active:scale-95 disabled:opacity-50"
              >
                {isSubmitting ? '...' : <Send size={14} />}
              </button>
            </form>
          </div>
        </div>

        {/* AdSense Placement */}
        {/* <div style={{ marginBottom: '32px', overflow: 'hidden', borderRadius: '12px' }}>
          <BannerAd />
        </div> */}

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-border flex flex-col md:flex-row justify-between items-center gap-5">
          <Link to="/" className="flex items-center gap-2.5 no-underline">
            <img src={logo} alt="SnapED Logo" className="h-8 w-auto" />
          </Link>
          <p className="text-muted-foreground text-[10px] font-black uppercase tracking-widest opacity-40">
            © {new Date().getFullYear()} SnapED codeCampus. All rights reserved.
          </p>
          <div className="flex gap-6">
            <FooterLink to="/terms-conditions">Terms</FooterLink>
            <FooterLink to="/privacy-policy">Privacy</FooterLink>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
